<script setup lang="ts">
import TtContainer from '@/components/TtContainer.vue'
import TtButton from '@/components/TtButton.vue'
import IconClose from '@/components/Icon/Close.vue'
import IconSpinner from '@/components/Icon/Spinner.vue'
import { useI18n } from 'vue-i18n'
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { DeleteAccountResponse, useAccountStore } from '@/store/account'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const accountStore = useAccountStore()
const router = useRouter()
const { logout, deleteAccount: deleteUserAccount } = accountStore

const status = ref<'idle' | 'loading' | 'error'>('idle')
const errorMessage = ref('')
const roleError = ref(false)

const close = () => {
  if (status.value === 'loading') return
  emit('close')
}

const deleteAccount = async () => {
  const blockedRole = accountStore.user?.roles?.find((role) =>
    ['siteowner', 'admin', 'companyadmin'].includes(role.type)
  )?.type
  if (blockedRole) {
    const roleLabel =
      blockedRole === 'siteowner'
        ? t('profile.siteownerRole')
        : blockedRole === 'admin'
          ? t('profile.adminRole')
          : t('profile.companyadminRole')
    errorMessage.value = t('profile.terminateAccountRoleError', { role: roleLabel })
    roleError.value = true
    status.value = 'error'
    return
  }

  errorMessage.value = ''
  roleError.value = false
  status.value = 'loading'
  const deleteResult = await deleteUserAccount()
  if (deleteResult !== DeleteAccountResponse.Success) {
    if (deleteResult === DeleteAccountResponse.RoleNotAllowed) {
      errorMessage.value = t('profile.terminateAccountRoleError', {
        role: t('profile.currentRole')
      })
      roleError.value = true
    } else {
      errorMessage.value = t('profile.terminateAccountError')
      roleError.value = false
    }
    status.value = 'error'
    return
  }

  router.push({ name: 'login' })
  logout()
  emit('close')
}

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) {
      status.value = 'idle'
      errorMessage.value = ''
      roleError.value = false
    }
  }
)

const { t } = useI18n({ useScope: 'global' })
</script>

<template>
  <TtContainer
    :open="open"
    variant="popup-card-bottom"
    class="z-50"
    @set-open="(val) => !val && close()"
  >
    <template #header>
      <div class="relative grid w-full grid-cols-10 px-5 pb-2 pt-5">
        <h2
          class="col-span-9 text-2xl font-semibold leading-7 tracking-tight text-black"
        >
          {{ t('profile.terminateAccountPopupTitle') }}
        </h2>
        <button v-if="status !== 'loading'" type="button" @click="close">
          <IconClose class="ml-auto mr-0 h-3.5 w-3.5 text-tt-gray" />
        </button>
      </div>
      <p class="px-5 text-lg font-light tracking-tight">
        <template v-if="status === 'error'">
          {{ errorMessage || t('profile.terminateAccountError') }}
        </template>
        <template v-else-if="status === 'loading'">
          {{ t('profile.terminateAccountDeleting') }}
        </template>
        <template v-else>
          {{ t('profile.terminateAccountPopupDescription') }}
        </template>
      </p>
    </template>
    <div class="overflow-auto px-5 pb-8">
      <div v-if="status === 'loading'" class="mt-7 flex justify-center py-4">
        <span class="text-tt-gray" style="height: 1.5rem">
          <IconSpinner />
        </span>
      </div>
      <div v-else class="mt-7 grid gap-4">
        <TtButton
          type="danger"
          size="large"
          as="button"
          :disabled="roleError"
          @click="deleteAccount"
        >
          {{ t('profile.terminateAccount') }}
        </TtButton>
        <TtButton type="tertiary" size="large" as="button" @click="close">
          {{ t('location.cancel') }}
        </TtButton>
      </div>
    </div>
  </TtContainer>
</template>
